import { isToolName, type ToolName } from './flow/tools';

export const VOICE_OBSERVABILITY_EVENTS = {
  ready: 'galapagos.chat.voice_observability.ready',
  alignment: 'galapagos.chat.voice_alignment.timing',
  catchup: 'galapagos.chat.voice_alignment.catchup',
  toolLifecycle: 'galapagos.chat.tool_call.lifecycle',
  toolUi: 'galapagos.chat.tool_ui.milestone',
} as const;

export const VOICE_OBSERVABILITY_TIMELINE_STAGES = [
  'provider_requested',
  'provider_acknowledged',
  'handler_started',
  'handler_completed',
  'first_useful_visible',
  'fully_ready',
  'next_audio_onset',
] as const;

export type VoiceObservabilityTimelineStage = (typeof VOICE_OBSERVABILITY_TIMELINE_STAGES)[number];
export type BoundedVoiceToolName = ToolName | 'unknown_tool';
export const VOICE_VERIFICATION_PLATFORMS = ['ios', 'android'] as const;
export const VOICE_VERIFICATION_PROVIDER_MODES = ['elevenlabs_saas', 'backend2_vpc'] as const;
export const VOICE_VERIFICATION_LANES = VOICE_VERIFICATION_PLATFORMS.flatMap((platform) =>
  VOICE_VERIFICATION_PROVIDER_MODES.map((providerMode) => ({ platform, providerMode })),
);
export type VoiceVerificationPlatform = (typeof VOICE_VERIFICATION_PLATFORMS)[number] | 'web';
export type VoiceVerificationProviderMode = (typeof VOICE_VERIFICATION_PROVIDER_MODES)[number];

const MAX_VERIFICATION_SESSION_ID_LENGTH = 64;
const MAX_CONVERSATION_TOKEN_LENGTH = 19;
const MAX_LANE_PREFIX_LENGTH =
  MAX_VERIFICATION_SESSION_ID_LENGTH - MAX_CONVERSATION_TOKEN_LENGTH - 1;

export function boundedVoiceToolName(toolName: string): BoundedVoiceToolName {
  return isToolName(toolName) ? toolName : 'unknown_tool';
}

/**
 * Derive the query prefix for one platform/provider lane from the
 * workflow-built run-set seed.
 */
export function voiceVerificationSessionIdForLane(
  seed: string | undefined,
  platform: VoiceVerificationPlatform,
  providerMode: VoiceVerificationProviderMode,
): string | undefined {
  if (!seed) return undefined;
  const provider = providerMode === 'elevenlabs_saas' ? 'saas' : 'vpc';
  const suffix = `_${platform}_${provider}`;
  return `${seed.slice(0, MAX_LANE_PREFIX_LENGTH - suffix.length)}${suffix}`;
}

/**
 * Add a per-connection token to the lane prefix. A verifier can query the
 * prefix, select the newest readiness canary, then require all evidence to
 * carry this exact id so another conversation cannot satisfy the gate.
 */
export function voiceVerificationSessionIdForConversation(
  seed: string | undefined,
  platform: VoiceVerificationPlatform,
  providerMode: VoiceVerificationProviderMode,
  conversationToken: string,
): string | undefined {
  const lanePrefix = voiceVerificationSessionIdForLane(seed, platform, providerMode);
  const boundedToken = conversationToken
    .replace(/[^A-Za-z0-9_-]/g, '')
    .slice(0, MAX_CONVERSATION_TOKEN_LENGTH);
  if (!lanePrefix || !boundedToken) return undefined;
  return `${lanePrefix}_${boundedToken}`;
}
